import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldError, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Switch } from "@bednbody/ui/components/switch";
import { createManualGiftpassOrderSchema } from "@bednbody/types";
import { useState } from "react";
import { toast } from "sonner";
import { type GiftpassOrder, peso } from "./giftpass-orders.model";
import {
  useCancelOrder,
  useCreateManualOrder,
  useGiftpassPackages,
  useGiftpassReferrals,
  useMarkOrderPaid,
} from "./giftpass-orders.hooks";
import { EMPTY_PAYMENT, PaymentFields, type PaymentValue, paymentReady } from "./PaymentFields";

/** The server's message, plus the database error code when it sent one (never row data). */
function errorText(error: Error): string {
  const code = (error as { details?: { dbCode?: unknown } }).details?.dbCode;
  return typeof code === "string" ? `${error.message} (${code})` : error.message;
}

/** One order holds at most 5,000 passes (0373). */
const MAX_PASSES = 5000;

export function MarkPaidDialog({
  order,
  open,
  onOpenChange,
}: {
  order: GiftpassOrder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [payment, setPayment] = useState<PaymentValue>(EMPTY_PAYMENT);
  const markPaid = useMarkOrderPaid();
  // Closing forgets what was typed, so a reference or proof never carries over
  // to the next order this dialog opens for.
  const close = (next: boolean) => {
    if (!next) setPayment(EMPTY_PAYMENT);
    onOpenChange(next);
  };

  const submit = () =>
    markPaid.mutate(
      {
        orderId: order.id,
        method: payment.method,
        reference: payment.reference.trim(),
        proofPath: payment.proofPath,
      },
      {
        onSuccess: () => {
          toast.success(`${order.order_code} paid — ${order.total_passes} codes created`);
          close(false);
        },
        onError: (error) => toast.error(errorText(error)),
      },
    );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark {order.order_code} as paid</DialogTitle>
          <DialogDescription>
            {peso(Number(order.amount))} · {order.package_name}
            {order.quantity > 1 ? ` × ${order.quantity}` : ""}. Marking it paid creates{" "}
            {order.total_passes} codes straight away. This is logged with your name.
          </DialogDescription>
        </DialogHeader>
        <PaymentFields id="mark" value={payment} onChange={setPayment} />
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            Back
          </Button>
          <Button disabled={!paymentReady(payment) || markPaid.isPending} onClick={submit}>
            Mark as paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CancelOrderDialog({
  order,
  open,
  onOpenChange,
}: {
  order: GiftpassOrder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const cancel = useCancelOrder();
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel {order.order_code}?</AlertDialogTitle>
          <AlertDialogDescription>
            Only for an order that was never paid. It stays in the list as Cancelled.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={cancel.isPending}
            onClick={() =>
              cancel.mutate(order.id, {
                onSuccess: () => {
                  toast.success(`${order.order_code} cancelled`);
                  onOpenChange(false);
                },
                onError: (error) => toast.error(errorText(error)),
              })
            }
          >
            Cancel order
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const EMPTY = {
  packageId: "",
  quantity: "1",
  buyerName: "",
  buyerPhone: "",
  buyerEmail: "",
  physical: false,
  deliveryAddress: "",
  referralId: "",
};

export function ManualOrderDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (orderId: string) => void;
}) {
  const { data: packages = [] } = useGiftpassPackages();
  const { data: referrals = [] } = useGiftpassReferrals();
  const create = useCreateManualOrder();
  const [form, setForm] = useState(EMPTY);
  const [paid, setPaid] = useState(true);
  const [payment, setPayment] = useState<PaymentValue>(EMPTY_PAYMENT);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (key: keyof typeof EMPTY, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const choices = packages.filter((p) => p.active);
  const pkg = choices.find((p) => p.id === form.packageId) ?? choices[0];
  const quantity = Math.max(0, Number.parseInt(form.quantity, 10) || 0);
  const passes = pkg ? (pkg.passes + pkg.free_passes) * quantity : 0;
  const tooBig = passes > MAX_PASSES;

  const submit = () => {
    if (!pkg) return;
    const parsed = createManualGiftpassOrderSchema.safeParse({
      packageId: pkg.id,
      quantity,
      buyerName: form.buyerName,
      buyerPhone: form.buyerPhone,
      buyerEmail: form.buyerEmail,
      physical: form.physical,
      deliveryAddress: form.physical ? form.deliveryAddress : null,
      referralId: form.referralId || null,
      clientRequestId: crypto.randomUUID(),
      paid,
      method: paid ? payment.method : null,
      reference: paid ? payment.reference.trim() : null,
      proofPath: paid ? payment.proofPath : null,
    });
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(parsed.error.issues.map((issue) => [issue.path[0], issue.message])),
      );
      return;
    }
    setErrors({});
    create.mutate(parsed.data, {
      onSuccess: (result) => {
        toast.success(
          paid
            ? `${result.orderCode} created and paid — ${passes} codes created`
            : `${result.orderCode} created — awaiting transfer`,
        );
        setForm(EMPTY);
        setPayment(EMPTY_PAYMENT);
        onOpenChange(false);
        onCreated(result.orderId);
      },
      onError: (error) => toast.error(errorText(error)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New manual order</DialogTitle>
          <DialogDescription>
            For walk-in, corporate and bank-transfer orders, priced from the package. Already paid:
            the codes are created when you save. Not paid yet: it waits as Awaiting transfer.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor="mo-package">Package</FieldLabel>
            <NativeSelect
              id="mo-package"
              value={pkg?.id ?? ""}
              onChange={(event) => set("packageId", event.target.value)}
            >
              {choices.map((p) => (
                <NativeSelectOption key={p.id} value={p.id}>
                  {p.name} — {peso(Number(p.price))}
                  {p.hidden ? " (hidden)" : ""}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field data-invalid={errors.quantity ? true : undefined}>
            <FieldLabel htmlFor="mo-quantity">Quantity</FieldLabel>
            <Input
              id="mo-quantity"
              type="number"
              min={1}
              step={1}
              value={form.quantity}
              onChange={(event) => set("quantity", event.target.value)}
            />
            <FieldError>{errors.quantity}</FieldError>
          </Field>
          <Field className="col-span-2" data-invalid={errors.buyerName ? true : undefined}>
            <FieldLabel htmlFor="mo-name">Buyer name</FieldLabel>
            <Input
              id="mo-name"
              type="text"
              maxLength={120}
              autoComplete="off"
              value={form.buyerName}
              onChange={(event) => set("buyerName", event.target.value)}
            />
            <FieldError>{errors.buyerName}</FieldError>
          </Field>
          <Field data-invalid={errors.buyerPhone ? true : undefined}>
            <FieldLabel htmlFor="mo-phone">Mobile</FieldLabel>
            <Input
              id="mo-phone"
              type="tel"
              placeholder="09171234567"
              autoComplete="off"
              value={form.buyerPhone}
              onChange={(event) => set("buyerPhone", event.target.value)}
            />
            <FieldError>{errors.buyerPhone}</FieldError>
          </Field>
          <Field data-invalid={errors.buyerEmail ? true : undefined}>
            <FieldLabel htmlFor="mo-email">Email</FieldLabel>
            <Input
              id="mo-email"
              type="email"
              autoComplete="off"
              value={form.buyerEmail}
              onChange={(event) => set("buyerEmail", event.target.value)}
            />
            <FieldError>{errors.buyerEmail}</FieldError>
          </Field>
          <Field className="col-span-2">
            <FieldLabel htmlFor="mo-referral">Referred by</FieldLabel>
            <NativeSelect
              id="mo-referral"
              value={form.referralId}
              onChange={(event) => set("referralId", event.target.value)}
            >
              <NativeSelectOption value="">No one</NativeSelectOption>
              {referrals
                .filter((r) => r.active)
                .map((r) => (
                  <NativeSelectOption key={r.id} value={r.id}>
                    {r.name}
                  </NativeSelectOption>
                ))}
            </NativeSelect>
          </Field>
          <Field orientation="horizontal" className="col-span-2">
            <Switch
              id="mo-physical"
              checked={form.physical}
              onCheckedChange={(checked) => set("physical", checked)}
            />
            <FieldLabel htmlFor="mo-physical">Also send printed cards</FieldLabel>
          </Field>
          {form.physical ? (
            <Field className="col-span-2" data-invalid={errors.deliveryAddress ? true : undefined}>
              <FieldLabel htmlFor="mo-address">Delivery address</FieldLabel>
              <Input
                id="mo-address"
                type="text"
                maxLength={500}
                autoComplete="off"
                value={form.deliveryAddress}
                onChange={(event) => set("deliveryAddress", event.target.value)}
              />
              <FieldError>{errors.deliveryAddress}</FieldError>
            </Field>
          ) : null}
        </div>
        <Field orientation="horizontal">
          <Switch id="mo-paid" checked={paid} onCheckedChange={setPaid} />
          <FieldLabel htmlFor="mo-paid">Already paid</FieldLabel>
        </Field>
        {paid ? <PaymentFields id="mo-pay" value={payment} onChange={setPayment} /> : null}
        <p className="flex justify-between gap-3 rounded-lg bg-muted px-3 py-2 text-sm">
          <span>
            {pkg?.name ?? "—"} × {quantity} = {passes} passes
          </span>
          <b className="tabular-nums">{peso(pkg ? Number(pkg.price) * quantity : 0)}</b>
        </p>
        {tooBig ? (
          <p className="text-sm text-destructive">
            One order holds at most 5,000 passes. Enter a bigger order as several orders.
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Back
          </Button>
          <Button
            disabled={
              !pkg || quantity < 1 || tooBig || (paid && !paymentReady(payment)) || create.isPending
            }
            onClick={submit}
          >
            {paid ? "Create paid order" : "Create order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
